import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Dialog, Grid, Typography } from "@mui/material";

import Modal from "@/components/Modal";
import { InputField } from "@/components/InputField";
import Button from "@/components/Button";

import style from "./index.module.css";

import { addPatientDeliveryAddressApi } from "@/utils/apis/patient/medicine";
import { getLocation } from "@/utils";
import { INotifyEnum, notify } from "@/utils/toaster";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

import { IAddDeliveryAddressDetailEnums } from "@/models/patient";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchPatientDeliveryAddress,
  medicine,
} from "@/store/slices/patient/medicine";

interface IProps {
  isOpen?: boolean;
  closeHandler?: () => void;
  showDeliveryAddress?: boolean;
}

const initialState = {
  city: "",
  address: "",
  pincode: "",
};

const addDeliveryAddress = (state: any, action: any) => {
  switch (action.type) {
    case IAddDeliveryAddressDetailEnums.ADDRESS:
    case IAddDeliveryAddressDetailEnums.CITY:
    case IAddDeliveryAddressDetailEnums.PINCODE:
      return {
        ...state,
        [action.type]: action.value,
      };
    default:
      return state;
  }
};

const AddDeliveryAddress: React.FC<IProps> = (props) => {
  const { isOpen, closeHandler, showDeliveryAddress = false } = props;
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState<boolean>(true);

  const [state, dispatch] = useReducer(addDeliveryAddress, initialState);
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const { patientDeliveryAddress } = useAppSelector(medicine);
  const appDispatch = useAppDispatch();

  const savePatientDeliveryAddress = async () => {
    try {
      const location = getLocation();
      const addPatientDeliveryAddressResponse =
        await addPatientDeliveryAddressApi({
          city: state?.city,
          address: state?.address,
          pincode: state?.pincode,
          latitude: location?.latitude,
          longitude: location?.longitude,
        });
      if ("result" in addPatientDeliveryAddressResponse) {
        notify(INotifyEnum.SUCCESS, addPatientDeliveryAddressResponse?.message);
        appDispatch(fetchPatientDeliveryAddress());
        closeHandler && closeHandler();
      } else {
        notify(INotifyEnum.ERROR, addPatientDeliveryAddressResponse?.message);
      }
    } catch (error) {
      notify(INotifyEnum.ERROR, error?.message);
    }
  };

  const addDeliveryAddressJSX = () => (
    <Grid item className={style.container}>
      <Grid item className={style.titleContainer}>
        <Typography component={"p"}>Address for Home Delivery</Typography>
      </Grid>
      <Grid item className={style.inputContainer}>
        <InputField
          type="text"
          value={state?.city}
          onChange={(event) =>
            dispatch({
              type: IAddDeliveryAddressDetailEnums.CITY,
              value: event.target.value,
            })
          }
          label={"City"}
          placeholder={"City"}
          disabled={showDeliveryAddress}
          fullWidth
        />
        <InputField
          type="text"
          value={state?.address}
          onChange={(event) =>
            dispatch({
              type: IAddDeliveryAddressDetailEnums.ADDRESS,
              value: event.target.value,
            })
          }
          label={"Address"}
          placeholder={"Address"}
          disabled={showDeliveryAddress}
          fullWidth
        />
        <InputField
          type="text"
          value={state?.pincode}
          onChange={(event) =>
            dispatch({
              type: IAddDeliveryAddressDetailEnums.PINCODE,
              value: event.target.value,
            })
          }
          label={"Pincode"}
          placeholder={"Pincode"}
          disabled={showDeliveryAddress}
          fullWidth
        />
        {!showDeliveryAddress && (
          <Grid item>
            <Button
              disabled={isSaveBtnDisabled}
              btnTrigger={savePatientDeliveryAddress}
            >
              Save
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );

  const isSmallScreen = useMemo(() => {
    return isMobile || isTablet;
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (state?.city && state?.address && state?.pincode) {
      setIsSaveBtnDisabled(false);
    }
  }, [state]);

  useEffect(() => {
    if (
      patientDeliveryAddress &&
      Object.keys(patientDeliveryAddress).length > 0
    ) {
      dispatch({
        type: IAddDeliveryAddressDetailEnums.CITY,
        value: patientDeliveryAddress[IAddDeliveryAddressDetailEnums.CITY],
      });
      dispatch({
        type: IAddDeliveryAddressDetailEnums.ADDRESS,
        value: patientDeliveryAddress[IAddDeliveryAddressDetailEnums.ADDRESS],
      });
      dispatch({
        type: IAddDeliveryAddressDetailEnums.PINCODE,
        value: patientDeliveryAddress[IAddDeliveryAddressDetailEnums.PINCODE],
      });
    }
  }, [patientDeliveryAddress]);

  if (showDeliveryAddress) return addDeliveryAddressJSX();

  if (isSmallScreen) {
    return (
      <Modal isOpen={isOpen} closeHandler={closeHandler}>
        {addDeliveryAddressJSX()}
      </Modal>
    );
  }
  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={closeHandler}
      maxWidth="xs"
      fullWidth
    >
      {addDeliveryAddressJSX()}
    </Dialog>
  );
};

export default AddDeliveryAddress;
